
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { motion } from "framer-motion";

import ClubFilter from "@/components/clubs/ClubFilter";
import ClubsList from "@/components/clubs/ClubsList";
import CreateClubDialog from "@/components/clubs/CreateClubDialog";
import { useClubs } from "@/hooks/useClubs";

const Clubs = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  const {
    clubs,
    loading,
    selectedCategory,
    setSelectedCategory,
    joiningClubId,
    handleJoinClub,
    createClub,
    getUniqueCategories,
    filterClubs,
  } = useClubs();

  const clubCategories = getUniqueCategories();
  const filteredClubs = filterClubs(searchQuery);
  const searchApplied = searchQuery !== "" || selectedCategory !== null;

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-background via-background to-background/80">
      <main className="flex-grow container mx-auto px-4 py-8 md:py-12">
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="space-y-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-primary/90 to-primary/80 bg-clip-text text-transparent leading-tight">
              Join Clubs & Communities
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Connect with like-minded teens in clubs and communities centered around shared interests.
            </p>
          </motion.div>
          <motion.div 
            className="flex-shrink-0"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={() => setIsDialogOpen(true)} 
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Plus size={18} className="mr-2" /> Create Club
            </Button>
          </motion.div>
        </motion.div>

        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <ClubFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={clubCategories}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <ClubsList
            clubs={filteredClubs}
            loading={loading}
            joiningClubId={joiningClubId}
            handleJoinClub={handleJoinClub}
            onCreateClick={() => setIsDialogOpen(true)}
            searchApplied={searchApplied}
          />
        </motion.div>
      </main>

      <CreateClubDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={createClub}
      />
    </div>
  );
};

export default Clubs;
